import { apiClient } from '@/api/client';
import type {
  DashboardActivityResponse,
  DashboardSummaryQueryParams,
  DashboardSummaryResponse,
} from '@/types/shared';

/**
 * T81 dashboard contract (`packages/shared/src/contracts/dashboard.ts`). Both endpoints are
 * role-parameterized and Admin-only for this pass (403 for every other role — T85 only wires the
 * Admin widget set; `DashboardPage`'s role registry keeps every other role on the placeholder, so
 * these calls are never issued for a non-Admin caller). The two endpoints are deliberately
 * independent fetches (T82 §"one widget failing must never blank the dashboard" at
 * two-group granularity) — callers must fetch/retry them separately, never as one combined call.
 */

/**
 * GET /dashboard/summary — the 5 Admin stat cards (one cheap parallel-SQL group; succeeds/fails
 * as a unit). `params` is the T98 optional period selector (`period` + `from`/`to` for
 * `period: 'custom'`) — omit entirely (or pass `undefined`) for the pre-T98 default view (no
 * query string sent at all, not even empty params, so this is byte-for-byte the old request).
 */
export async function getDashboardSummary(
  params?: DashboardSummaryQueryParams,
): Promise<DashboardSummaryResponse> {
  const { data } = await apiClient.get<DashboardSummaryResponse>('/dashboard/summary', {
    params,
  });
  return data;
}

/** GET /dashboard/activity — the last-10 recent-activity feed, independent of `/summary`. */
export async function getDashboardActivity(): Promise<DashboardActivityResponse> {
  const { data } = await apiClient.get<DashboardActivityResponse>('/dashboard/activity');
  return data;
}
